import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const P = (id: number, w = 800, h = 500) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}&h=${h}&dpr=1`;

const SERVICES = [
  {
    id: 'nepali-nlp',
    img: P(4578660),
    icon: '🧠',
    title: 'Nepali NLP Engine',
    tagline: 'The language layer for Nepali AI applications',
    color: '#0891b2',
    description: 'Our Nepali NLP Engine provides core language processing capabilities tuned to Nepali-specific corpora — including formal Nepali, colloquial speech, and bureaucratic language. Unlike generic multilingual models, it handles Devanagari script nuances and local idiom correctly.',
    features: [
      'Tokenization and morphological analysis for Devanagari script',
      'Named Entity Recognition (people, places, organizations, dates)',
      'Sentiment analysis — positive, negative, neutral scoring',
      'Text classification with custom category training',
      'Keyword extraction and automatic summarization',
      'Language detection (Nepali vs. Hindi vs. English)',
    ],
    useCases: [
      'Automatically classify citizen complaints by department',
      'Analyze public feedback and social media sentiment',
      'Extract key data from Nepali news and government reports',
      'Build content moderation pipelines for Nepali platforms',
    ],
    whoFor: ['Government agencies', 'Media organizations', 'Banks', 'Social platforms'],
  },
  {
    id: 'chatbots',
    img: P(30530412),
    icon: '💬',
    title: 'Intelligent Chatbots',
    tagline: "Conversational AI that speaks Nepal's language",
    color: '#0f4c81',
    description: 'Deploy AI-powered chatbots that understand natural Nepali text and speech. Our platform handles multi-turn conversations, integrates with your existing systems, and can be deployed on websites, mobile apps, or messaging platforms like Viber and WhatsApp.',
    features: [
      'Natural language understanding in Nepali and Nepali-English code-switching',
      'Intent recognition and entity extraction',
      'Conversation flow builder (no-code interface)',
      'Integration with existing CRMs, databases, and APIs',
      'Escalation to human agents with full context handoff',
      'Analytics dashboard with conversation-level insights',
    ],
    useCases: [
      'Citizen service portal — answer common government queries 24/7',
      'Bank customer support — account inquiries, loan status, branch info',
      'Hospital appointment booking and symptom triage',
      'Agricultural advisory bot for farmers via SMS or WhatsApp',
    ],
    whoFor: ['Government portals', 'Banks & cooperatives', 'Hospitals & clinics', 'Utility companies'],
  },
  {
    id: 'document-intelligence',
    img: P(8297361),
    icon: '📄',
    title: 'Document Intelligence',
    tagline: 'Turn paper Nepal into digital Nepal',
    color: '#0891b2',
    description: 'Our Document Intelligence platform combines OCR for Devanagari script with AI-powered data extraction. We process both printed and handwritten Nepali documents, extract structured data, and integrate into your workflows — reducing manual data entry to near zero.',
    features: [
      'High-accuracy Devanagari OCR (printed and handwritten)',
      'Intelligent form field extraction with auto-detection',
      'Batch document processing — hundreds of pages per hour',
      'Data validation and error flagging',
      'Integration with government EDMS and databases',
      'Full audit trail for every processed document',
    ],
    useCases: [
      'Digitize and make searchable land cadastral records',
      'Process citizenship and passport applications automatically',
      'Extract data from handwritten hospital records',
      'Automate legal contract data extraction and review',
    ],
    whoFor: ['Land registries', 'Immigration departments', 'Hospitals', 'Law firms & courts'],
  },
  {
    id: 'translation',
    img: P(135129),
    icon: '🔤',
    title: 'Translation APIs',
    tagline: 'Accurate, domain-aware translation for professional use',
    color: '#0f4c81',
    description: 'Our Translation API goes beyond generic machine translation — we offer domain-specific fine-tuning for legal, medical, and government terminology, ensuring translations that are accurate in context, not just literally correct.',
    features: [
      'Nepali ↔ English ↔ Hindi translation',
      'Domain-specific fine-tuning (legal, medical, government, financial)',
      'REST API with SDKs for Python, JavaScript, and PHP',
      'Batch translation — upload files, receive translated output',
      'Translation memory for consistency across documents',
      'Human review workflow integration',
    ],
    useCases: [
      'Translate official government circulars and gazette notices',
      'Multilingual patient forms and medical reports',
      'NGO and INGO report translation',
      'Localize websites and apps for Nepali audiences',
    ],
    whoFor: ['Government ministries', 'Hospitals & health orgs', 'NGOs & INGOs', 'Media companies'],
  },
  {
    id: 'speech',
    img: P(8105748),
    icon: '🎙️',
    title: 'Speech Solutions',
    tagline: 'Voice-first AI for Nepali speakers',
    color: '#0891b2',
    description: 'Our Speech platform provides Nepali Text-to-Speech (TTS) and Speech-to-Text (STT) with natural-sounding voices and high transcription accuracy. Ideal for IVR systems, accessibility tools, and voice-enabled applications serving citizens who may not be able to read.',
    features: [
      'Nepali TTS with natural prosody (multiple voice styles)',
      'Real-time Nepali STT with auto-punctuation',
      'IVR integration (Avaya, Asterisk, and open-source PBX)',
      'Streaming transcription for live applications',
      'Support for regional accents and dialects',
      'SSML support for fine-grained speech control',
    ],
    useCases: [
      'Bank and government IVR phone systems in Nepali',
      'Accessibility features for visually impaired citizens',
      'Voice-enabled health advisory for rural communities',
      'Automated broadcast and announcement systems',
    ],
    whoFor: ['Telecom operators', 'Banks', 'Government call centers', 'Health organizations'],
  },
  {
    id: 'automation',
    img: P(3862599),
    icon: '⚙️',
    title: 'AI Automation',
    tagline: 'Eliminate manual work with intelligent workflows',
    color: '#0f4c81',
    description: 'We integrate AI into your existing business processes to automate repetitive, document-heavy tasks. From invoice processing to application screening, our automation platform reduces processing time by up to 80% and virtually eliminates data entry errors.',
    features: [
      'Document ingestion and classification (email, scanner, API)',
      'Data extraction and validation pipelines',
      'Workflow automation with configurable approval routing',
      'Integration with existing ERP, EDMS, and databases',
      'Exception handling with human-in-the-loop escalation',
      'Reporting and compliance audit dashboards',
    ],
    useCases: [
      'Automate procurement and invoice processing',
      'Screen job or scholarship applications at volume',
      'Automate tax and compliance report generation',
      'Streamline loan application processing at banks',
    ],
    whoFor: ['Government agencies', 'Banks & financial institutions', 'Large enterprises', 'Academic institutions'],
  },
  {
    id: 'agentic-rag',
    img: P(8386440),
    icon: '🔍',
    title: 'Agentic RAG Systems',
    tagline: 'Knowledge that retrieves, reasons, and answers — not just searches',
    color: '#0891b2',
    description: 'Traditional search finds keywords. Agentic RAG builds AI systems that autonomously query multiple knowledge sources, evaluate what they found, refine queries if needed, and synthesize a grounded, cited answer — all without human direction. Works over your existing Nepali documents, databases, and policy libraries.',
    features: [
      'Multi-source retrieval — documents, databases, APIs, and knowledge graphs in one query',
      'Autonomous query planning: the agent decides what to retrieve and in what order',
      'Hybrid search — semantic similarity + keyword over Nepali content',
      'Source citation and confidence scoring on every answer',
      'Self-evaluation loop — agent verifies its own answers before responding',
      'Deploy over existing document repositories (no migration required)',
    ],
    useCases: [
      'Citizen portal: answer questions about government regulations and procedures in plain Nepali',
      'Bank officer support — query loan policies, compliance rules, and product info instantly',
      'Legal research over case law and statutes in Nepali',
      'Rural health worker assistant querying treatment protocols and drug guidelines',
    ],
    whoFor: ['Government portals', 'Banks & cooperatives', 'Law firms & courts', 'Health organizations', 'NGOs & INGOs'],
  },
  {
    id: 'agentic-workflows',
    img: P(8386437),
    icon: '🤖',
    title: 'Agentic AI Workflows',
    tagline: 'AI that plans, acts, and completes — not just responds',
    color: '#0f4c81',
    description: 'Agentic AI goes beyond chatbots and scripts. Autonomous agents break complex tasks into steps, call the right tools, handle exceptions intelligently, and complete multi-stage workflows end-to-end. Multi-agent architectures coordinate specialized agents in parallel — planner, executor, validator — for enterprise-scale automation that adapts to real-world messiness.',
    features: [
      'Multi-agent orchestration: planner, executor, and validator agents working in coordination',
      'Tool use: databases, web search, file systems, APIs, email, and government portals',
      'Long-horizon task planning — handles workflows that take minutes, not milliseconds',
      'Adaptive exception handling with intelligent human escalation when needed',
      'Full decision audit trail — every agent action logged with reasoning',
      'Integration with existing ERP, EDMS, and government IT systems',
    ],
    useCases: [
      'End-to-end application processing: intake → document review → decision → citizen notification',
      'Autonomous compliance monitoring — agents scan regulations, flag gaps, and draft reports',
      'Loan processing agent: collects documents, verifies against criteria, scores risk, routes for approval',
      'Policy research agent: gathers data from multiple sources, synthesizes findings, drafts reports',
    ],
    whoFor: ['Government agencies', 'Banks & insurance companies', 'Large enterprises', 'Development organizations'],
  },
];

export function ServicesPage() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="tab-content">
      <section className="hero-section">
        <h1 className="hero-title">Our Services</h1>
        <p className="hero-subtitle">
          Six core AI & NLP capabilities — each built specifically for Nepali language, culture, and organizational context.
        </p>
      </section>

      <section className="section">
        {SERVICES.map(service => (
          <div
            key={service.id}
            className="card"
            style={{ marginBottom: '1.5rem', borderRadius: '16px', padding: 0, overflow: 'hidden' }}
          >
            {/* Image banner */}
            <div style={{
              height: 180,
              backgroundImage: `url(${service.img})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute', inset: 0,
                background: `linear-gradient(to right, ${service.color}e8 0%, ${service.color}99 60%, transparent 100%)`,
              }}/>
              <div style={{ position: 'absolute', bottom: 16, left: 20, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '2rem' }}>{service.icon}</span>
                <div>
                  <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'white', margin: 0 }}>{service.title}</h2>
                  <p style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, fontSize: '0.85rem', margin: 0 }}>{service.tagline}</p>
                </div>
              </div>
            </div>

            <div style={{ padding: '1.5rem 2rem 2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                <p style={{ color: '#64748b', lineHeight: 1.65, flex: 1, minWidth: 240 }}>{service.description}</p>
                <button
                  onClick={() => setExpanded(expanded === service.id ? null : service.id)}
                  style={{
                    padding: '0.5rem 1.25rem',
                    background: expanded === service.id ? service.color : 'transparent',
                    color: expanded === service.id ? 'white' : service.color,
                    border: `1.5px solid ${service.color}`,
                    borderRadius: '20px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {expanded === service.id ? 'Hide Details' : 'See Details'}
                </button>
              </div>

            {expanded === service.id && (
              <div style={{ marginTop: '1.5rem', borderTop: '1px solid #f1f5f9', paddingTop: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
                  <div>
                    <h4 style={{ color: '#1e293b', fontWeight: 700, marginBottom: '0.75rem' }}>Key Features</h4>
                    <ul style={{ paddingLeft: '1.25rem', color: '#64748b', lineHeight: 2 }}>
                      {service.features.map(f => <li key={f}>{f}</li>)}
                    </ul>
                  </div>
                  <div>
                    <h4 style={{ color: '#1e293b', fontWeight: 700, marginBottom: '0.75rem' }}>Use Cases</h4>
                    <ul style={{ paddingLeft: '1.25rem', color: '#64748b', lineHeight: 2, marginBottom: '1rem' }}>
                      {service.useCases.map(u => <li key={u}>{u}</li>)}
                    </ul>
                    <h4 style={{ color: '#1e293b', fontWeight: 700, marginBottom: '0.75rem' }}>Who It's For</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {service.whoFor.map(w => (
                        <span
                          key={w}
                          style={{
                            background: service.color === '#0891b2' ? 'rgba(8,145,178,0.1)' : 'rgba(15,76,129,0.1)',
                            color: service.color,
                            padding: '0.25rem 0.75rem',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                          }}
                        >
                          {w}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        ))}
      </section>

      <section className="section">
        <div className="card" style={{ background: 'linear-gradient(135deg, #0f4c81 0%, #0891b2 100%)', color: 'white', textAlign: 'center' }}>
          <h2 style={{ color: 'white', marginBottom: '0.75rem' }}>Not sure which service fits your needs?</h2>
          <p style={{ opacity: 0.9, marginBottom: '1.5rem' }}>
            Schedule a free 30-minute consultation and we'll map your challenges to the right AI solution.
          </p>
          <button
            onClick={() => navigate('/contact')}
            style={{ padding: '0.875rem 2rem', background: 'white', color: '#0f4c81', border: 'none', borderRadius: '25px', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' }}
          >
            Book a Free Consultation
          </button>
        </div>
      </section>
    </div>
  );
}
